import React, { useEffect, useMemo, useState } from "react";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { Box, Stack, Typography } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import theme from "Theme/AppTheme";
import { SignalsEmailCadence } from "../../SignalsModels";
import { useCurrentDashboard } from "../../useCurrentDashboard";
import { useStore } from "utils/useStore";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import {
    ScheduleEmailRequest,
    ScheduleEmailResponse,
    SignalsService,
} from "services/SignalsService";
import { ApplicationFilterDomain } from "services/ApplicationFiltersService";
import { ServiceError } from "services/BaseService";

export const DashboardScheduleEmailDialog = ({
    open,
    setOpen,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
}) => {
    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const messageStore = useStore(MessageStore);
    const currentDashboard = useCurrentDashboard();

    const signalsService = useMemo(() => new SignalsService(), []);

    const [cadence, setCadence] = useState<SignalsEmailCadence>(
        SignalsEmailCadence.Immediate,
    );
    const [newFilterName, setNewFilterName] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);

    const isImmediate = cadence === SignalsEmailCadence.Immediate;
    const message = cadenceToAdditionalMessage[cadence];

    const isStandardDashboard = !!currentDashboard?.isStandardDashboard;
    const isViewOnly = !currentDashboard?.dashboardIsEditable;

    const currentlyPinnedFilter = applicationFiltersStore.savedFilters.find(
        (f) => f.id === currentDashboard?.pinnedApplicationFilterId,
    );

    useEffect(() => {
        if (!open) {
            return;
        }
        if (!!currentlyPinnedFilter) {
            setNewFilterName(currentlyPinnedFilter.name);
        } else if (!!applicationFiltersStore.selectedSavedFilter) {
            setNewFilterName(applicationFiltersStore.selectedSavedFilter.name);
        } else {
            setNewFilterName("");
        }
    }, [
        open,
        currentlyPinnedFilter,
        applicationFiltersStore.selectedSavedFilter,
    ]);

    useEffect(() => {
        if (open) {
            setCadence(SignalsEmailCadence.Immediate);
        }
    }, [open]);

    if (!currentDashboard) {
        return null;
    }

    const filterNameFieldDisabled =
        !!currentDashboard?.pinnedApplicationFilterId ||
        !!applicationFiltersStore.selectedSavedFilter ||
        !applicationFiltersStore.appliedFilterCount ||
        isStandardDashboard ||
        isViewOnly;

    const filterNameCollision =
        !!applicationFiltersStore.savedFilters.find(
            (f) => f.name === newFilterName,
        ) && !applicationFiltersStore.selectedSavedFilter;

    const newFilterNameNotValid = filterNameCollision || newFilterName === "";

    const isViewOnlyAndAttemptingToUpdatePin =
        isViewOnly &&
        !currentlyPinnedFilter &&
        (!!applicationFiltersStore.selectedSavedFilter ||
            !!applicationFiltersStore.appliedFilterCount);

    const filterNameInputValue = !applicationFiltersStore.appliedFilterCount
        ? "No filters applied to this dashboard."
        : isStandardDashboard
        ? "This is a standard dashboard, so your current filters won't be applied to the recurring dashboard email."
        : isViewOnlyAndAttemptingToUpdatePin
        ? "You have view-only access, so your current filters won't be applied to the recurring dashboard email."
        : newFilterName;

    const warningMessage = isStandardDashboard
        ? "This is a standard dashboard, so your current filters won't be applied to the recurring dashboard email."
        : isViewOnlyAndAttemptingToUpdatePin
        ? "You have view-only access, so your current filters won't be applied to the recurring dashboard email."
        : "";

    const isScheduleDisabled =
        !isImmediate &&
        !!applicationFiltersStore.appliedFilterCount &&
        !currentDashboard?.pinnedApplicationFilterId &&
        !applicationFiltersStore.selectedSavedFilter &&
        !isStandardDashboard &&
        !isViewOnly &&
        newFilterNameNotValid;

    async function handleSend() {
        if (!currentDashboard) return;
        setIsLoading(true);

        try {
            let emailPromise: Promise<ScheduleEmailResponse>;

            if (isImmediate) {
                emailPromise = signalsService.sendImmediateDashboardEmail(
                    currentDashboard.id,
                    applicationFiltersStore.toRequestObject(),
                    currentDashboard.getImmediateEmailObjectOverrides(),
                );
            } else {
                const scheduleEmailRequest: ScheduleEmailRequest = {
                    dashboardId: currentDashboard.id,
                    cadence,
                };

                const shouldCreateAndPinFilter =
                    !currentlyPinnedFilter &&
                    !!applicationFiltersStore.appliedFilterCount &&
                    !applicationFiltersStore.selectedSavedFilter &&
                    !isStandardDashboard &&
                    !isViewOnly;

                const shouldPinFilter =
                    !currentlyPinnedFilter &&
                    !!applicationFiltersStore.selectedSavedFilter &&
                    !isStandardDashboard &&
                    !isViewOnly;

                if (shouldCreateAndPinFilter) {
                    scheduleEmailRequest.domain =
                        ApplicationFilterDomain.Organization;
                    scheduleEmailRequest.name = newFilterName;
                    scheduleEmailRequest.value =
                        applicationFiltersStore.toRequestObject();
                } else if (shouldPinFilter) {
                    scheduleEmailRequest.filterIdToPin =
                        applicationFiltersStore.selectedSavedFilter!.id;
                }

                emailPromise =
                    signalsService.scheduleDashboardEmail(scheduleEmailRequest);
            }

            const response = await emailPromise;
            setOpen(false);

            if (!isImmediate) {
                currentDashboard.signalsDashboardSubscriptionId =
                    response.newSubscriptionId;

                if (
                    !currentlyPinnedFilter &&
                    !isStandardDashboard &&
                    !isViewOnly
                ) {
                    const newFilterId =
                        response.newFilterId ??
                        applicationFiltersStore.selectedSavedFilter?.id;
                    currentDashboard.pinnedApplicationFilterId = newFilterId;

                    if (
                        !applicationFiltersStore.selectedSavedFilter &&
                        response.newFilterId
                    ) {
                        await applicationFiltersStore.loadSavedFiltersByDomain();
                        applicationFiltersStore.selectedSavedFilter =
                            applicationFiltersStore.savedFilters.find(
                                (f) => f.id === response.newFilterId,
                            );
                    }
                }
            }

            messageStore.logMessage(response.message, "success");
        } catch (error) {
            if (error instanceof ServiceError) {
                messageStore.logError(error.errorMessage ?? error.message);
            } else if (error instanceof Error) {
                messageStore.logError(error.message);
            } else {
                messageStore.logError(
                    "An error occurred while scheduling your email.",
                );
            }
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <AcxDialog
            isOpen={open}
            onClose={() => setOpen(false)}
            title="Email this Dashboard"
            subTitle="Receive tailored insights and updates on the data that matters most to you."
            maxWidth="xl"
            hideCancelButton
            disableEnforceFocus
            dialogContentChildren={
                <Stack direction="column">
                    <AcxSelectSingle
                        id={"email-frequency"}
                        valueField="value"
                        labelField="label"
                        options={selectOptions}
                        fullWidth
                        inputLabel="Frequency"
                        defaultValue={selectOptions.find(
                            (o) => o.value === cadence,
                        )}
                        onChange={(o) => setCadence(o.value)}
                        isDisabled={selectOptions.length <= 1}
                    />
                    {!!message && (
                        <Typography
                            sx={(theme) => ({
                                color: theme.palette.gray[500],
                                pt: 1,
                            })}
                        >
                            {message}
                        </Typography>
                    )}
                    {!isImmediate && (
                        <Box marginTop={theme.spacing(2)}>
                            {warningMessage ? (
                                <Typography
                                    sx={(theme) => ({
                                        color: theme.palette.gray[500],
                                        pt: 1,
                                    })}
                                >
                                    {warningMessage}
                                </Typography>
                            ) : (
                                <AcxMainTextField
                                    id="save-filter"
                                    labelText="Pinned Filter Name"
                                    value={filterNameInputValue}
                                    onChange={(e) => {
                                        setNewFilterName(e.target.value);
                                    }}
                                    isDisabled={filterNameFieldDisabled}
                                    error={filterNameCollision}
                                    errorHelper={
                                        filterNameCollision
                                            ? filterExistsWarning
                                            : ""
                                    }
                                    required={
                                        !!applicationFiltersStore.appliedFilterCount &&
                                        !filterNameInputValue &&
                                        !isStandardDashboard &&
                                        !isViewOnly
                                    }
                                    textareaRootStyles={{ marginRight: 0 }}
                                />
                            )}
                            {!currentlyPinnedFilter &&
                                !!applicationFiltersStore.appliedFilterCount &&
                                !isStandardDashboard &&
                                !isViewOnly && (
                                    <Typography
                                        sx={(theme) => ({
                                            color: theme.palette.gray[500],
                                            pt: 1,
                                        })}
                                    >
                                        To make sure your emails match what you
                                        see here, your applied filters will be
                                        pinned to this dashboard.
                                    </Typography>
                                )}
                        </Box>
                    )}
                </Stack>
            }
        >
            <AcxButton
                loading={isLoading}
                onClick={handleSend}
                disabled={isScheduleDisabled}
                disabledTooltip={filterNameCollision ? filterExistsWarning : ""}
                id={
                    isImmediate
                        ? "email-dashboard-send"
                        : "email-dashboard-schedule"
                }
            >
                {isImmediate ? "Send" : "Schedule"}
            </AcxButton>
        </AcxDialog>
    );
};

const selectOptions = [
    {
        label: "Once Immediately",
        value: SignalsEmailCadence.Immediate,
        id: "email-dashboard-frequency-once-immediately",
    },
    {
        label: "Weekly",
        value: SignalsEmailCadence.Weekly,
        id: "email-dashboard-frequency-weekly",
    },
    {
        label: "Monthly",
        value: SignalsEmailCadence.Monthly,
        id: "email-dashboard-frequency-monthly",
    },
];

const cadenceToAdditionalMessage: Record<SignalsEmailCadence, string> = {
    [SignalsEmailCadence.Immediate]:
        "This dashboard will be sent to your email immediately, and will show the current data.",
    // Daily not implemented
    [SignalsEmailCadence.Daily]: "",
    [SignalsEmailCadence.Weekly]:
        "This dashboard will be sent each Monday morning, and will show the last completed week of data.",
    [SignalsEmailCadence.Monthly]:
        "This dashboard will be sent on the first business day of each month, and will show the last completed month of data.",
};

const filterExistsWarning =
    "Filter name already exists. Try a different name.";
